import React from "react";

import DashboardSummaryCard from "./DashboardSummaryCard";
import UniformCard from "./UniformCard";

import LivePreview from "./LivePreview";
import PlaybackPreview from "./PlaybackPreview";
import DevicePreview from "./DevicePreview";
import EventPreview from "./EventPreview";
import MapPreview from "./MapPreview";
import AiSafetyPreview from "./AiSafetyPreview";
import PriorityPreviewCard from "./PriorityPreviewCard";

import {
  CameraIcon,
  PlaybackIcon,
  DeviceIcon,
  EventIcon,
  MapIcon,
} from "./DashboardIcons";

export default function LegacyDashboard({
  me,
  permissionFlags,
  cameras,
  camerasLoading,
  dangerText,
  dangerCount,
  logs,
  onNavigate,
  summary,
  licenseFeatures = {},
  activePpeCount = 0,
  onPpeDangerClick,
  onDangerClick,
}) {
  const totalCameras =
    summary?.totalCameras ?? (Array.isArray(cameras) ? cameras.length : 0);

  const activeCameras = summary?.activeCameras ?? 0;
  const todayEvents = summary?.todayEvents ?? 0;

  const flags = permissionFlags || {};

  const showCameraSummary = flags.canLiveView;
  const showDeviceSummary = flags.canDevices;

  const canSeeEvents = Boolean(flags.canEvents || flags.canEventData);
  const canUseWatchSummary = Boolean(flags.canWatchDanger);

  const ppeLicensed = Boolean(licenseFeatures?.ppeEnabled);
  const ppeActive = Number(activePpeCount || 0);

  // "오늘 이벤트" = 오늘 전체 이벤트(PPE 포함). 이벤트 열람 권한 기준.
  const displayTodayEvents = canSeeEvents ? todayEvents : 0;

  // "위험 감지" 카드: PPE 라이선스 현장이면 '미확인 미착용 건수'(사람이 확인할 때까지 유지),
  // 아니면 민트워치 SOS(기존).
  const dangerIsPpe = ppeLicensed && canSeeEvents;
  const displayDangerCount = dangerIsPpe
    ? ppeActive
    : canUseWatchSummary
    ? Number(dangerCount || 0)
    : 0;
  // 워치 SOS 위험은 SOS 해제 모달. PPE는 확인(결재) 모달.
  const hasWatchDanger = !dangerIsPpe && canUseWatchSummary && displayDangerCount > 0;
  const ppeAlert = dangerIsPpe && ppeActive > 0;

  const showSummarySection = showCameraSummary || showDeviceSummary || flags.canEvents;

  const showCards = [
    flags.canLiveView,
    flags.canPlayback,
    flags.canLiveView,
    flags.canDevices,
    flags.canEvents,
    flags.canMap,
  ].some(Boolean);

  return (
    <div className="space-y-4 overflow-visible">
      {showSummarySection && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 [&>*]:min-h-[104px] [&>*]:py-4">
          <DashboardSummaryCard
            label="전체 카메라"
            value={camerasLoading ? "..." : `${totalCameras}대`}
            accent="text-blue-600 dark:text-blue-300"
            subLabel="등록된 카메라 수"
          />

          <DashboardSummaryCard
            label="활성 장비"
            value={camerasLoading ? "..." : `${activeCameras}대`}
            accent="text-emerald-600 dark:text-emerald-300"
            subLabel="현재 사용 가능한 카메라"
          />

          <DashboardSummaryCard
            label="위험 감지"
            value={`${displayDangerCount}건`}
            accent="text-amber-500 dark:text-amber-300"
            subLabel={
              dangerIsPpe
                ? "미확인 안전장구 미착용"
                : canUseWatchSummary
                ? dangerText || "현재 위험 상태를 표시합니다."
                : "민트워치 연동 시 위험 상태 표시"
            }
            danger={hasWatchDanger || ppeAlert}
            clickable={hasWatchDanger || ppeAlert}
            actionHint={ppeAlert ? "클릭하여 확인 처리" : "클릭하여 위험 해제"}
            onClick={
              ppeAlert
                ? onPpeDangerClick
                : hasWatchDanger
                ? onDangerClick
                : undefined
            }
          />

          <DashboardSummaryCard
            label="오늘 이벤트"
            value={`${displayTodayEvents}건`}
            accent="text-rose-500 dark:text-rose-300"
            subLabel={
              canSeeEvents
                ? "오늘 기록된 전체 이벤트 수"
                : "이벤트 열람 권한이 없습니다"
            }
          />
        </section>
      )}

      {showCards ? (
        <section className="grid grid-cols-1 gap-3 overflow-visible md:grid-cols-2 xl:grid-cols-3 [&>*]:min-h-[300px]">
          {flags.canLiveView && (
            <UniformCard
              title="실시간 관제"
              description="현장 카메라 상태와 라이브 영상을 실시간으로 확인합니다."
              actionLabel="실시간 관제 열기"
              onClick={() => onNavigate?.("live")}
              icon={<CameraIcon />}
              theme="blue"
              preview={<LivePreview cameras={cameras} />}
            />
          )}

          {flags.canPlayback && (
            <UniformCard
              title="다시보기"
              description="특정 시간대 녹화 영상을 조회하고 필요한 구간을 재생 또는 다운로드합니다."
              actionLabel="Playback 열기"
              onClick={() => onNavigate?.("playback")}
              icon={<PlaybackIcon />}
              theme="slate"
              preview={<PlaybackPreview />}
            />
          )}

          {flags.canLiveView && (
            <PriorityPreviewCard cameras={cameras} onNavigate={onNavigate} />
          )}

          {flags.canDevices && (
            <UniformCard
              title="장비 관리"
              description="카메라 및 장비 상태를 확인하고 등록 관리 영역으로 확장할 수 있습니다."
              actionLabel="장비 메뉴 열기"
              onClick={() => onNavigate?.("devices")}
              icon={<DeviceIcon />}
              theme="violet"
              preview={<DevicePreview />}
            />
          )}

          {flags.canEvents && (
            <UniformCard
              title="이벤트 내역"
              description="화재, 위험 알림, 센서 이벤트를 빠르게 확인하고 최근 기록을 조회합니다."
              actionLabel="이벤트 보기"
              onClick={() => onNavigate?.("events")}
              icon={<EventIcon />}
              theme="red"
              preview={<EventPreview logs={logs} />}
            />
          )}

          {/*
            지도 카드 — MapPreview·MapIcon 을 import 해놓고 그리는 코드가 빠져 있었다.
            카드 노출 조건(showCards)에는 flags.canMap 이 들어 있어서, 지도 권한만 있는
            계정은 «카드가 있다»고 판단해 놓고 정작 아무것도 안 보이는 상태였다(2026-10-13 수정).
          */}
          {flags.canMap && (
            <UniformCard
              title="지도"
              description="민트캠의 현재 위치를 지도에서 확인합니다."
              actionLabel="지도 열기"
              onClick={() => onNavigate?.("map")}
              icon={<MapIcon />}
              theme="amber"
              preview={<MapPreview />}
            />
          )}

          {ppeLicensed && canSeeEvents && (
            <UniformCard
              title="AI 안전 감지"
              description="AI가 CCTV 영상을 실시간 분석해 안전장구(안전모·조끼·하네스) 미착용을 자동 감지합니다."
              actionLabel="안전 리포트 열기"
              onClick={() => onNavigate?.("safety-report")}
              theme="ai"
              badge="AI"
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className="h-6 w-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              }
              preview={<AiSafetyPreview cameraCount={totalCameras} />}
            />
          )}
        </section>
      ) : (
        <section className="rounded-[30px] border border-white/70 bg-white p-6 text-center shadow-[0_16px_40px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-[#111827]/82">
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            표시할 대시보드 카드가 없습니다.
          </div>

          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            현재 계정 권한 기준으로 노출 가능한 카드가 없거나 제한되어 있습니다.
          </div>
        </section>
      )}
    </div>
  );
}